import { z } from 'zod'
import type { StatusTone } from '@/design-system'
import { consentStates } from './smsConsents'
import { journalStates } from './smsJournal'
import { reasonLabel } from './smsReasons'

/**
 * Boîte de réception SMS (`sms_inbox_v1`, `sms_conversation_v1`). Le non-lu est celui de l'équipe dans RECA : les
 * SMS n'ont pas d'accusé de lecture. Lecture et résolution sont distinctes.
 */

export const conversationStatuses = ['TO_DO', 'IN_PROGRESS', 'WAITING_CUSTOMER', 'RESOLVED'] as const
export type ConversationStatus = (typeof conversationStatuses)[number]

export const inboxViews = ['TO_DO', 'UNREAD', 'MINE', 'ALL'] as const
export type InboxView = (typeof inboxViews)[number]

const identitySchema = z.object({
  kind: z.enum(['KNOWN', 'SHARED', 'TO_CONFIRM', 'UNKNOWN']),
  clients: z.array(
    z.object({ clientId: z.string(), name: z.string(), address: z.string().nullable().optional() }),
  ),
  candidates: z.array(z.object({ clientId: z.string(), name: z.string() })),
})
export type ContactIdentity = z.infer<typeof identitySchema>

const inboxRowSchema = z.object({
  conversationId: z.string(),
  contactId: z.string(),
  phone: z.string(),
  status: z.enum(conversationStatuses),
  assignedUserId: z.string().nullable(),
  assignedUserName: z.string().nullable(),
  assignedToMe: z.boolean(),
  lastMessageAt: z.string(),
  version: z.number().int(),
  unreadCount: z.number().int(),
  blocked: z.boolean(),
  identity: identitySchema,
  preview: z
    .object({
      type: z.enum(['INBOUND', 'OUTBOUND', 'NOTE']),
      body: z.string(),
      at: z.string(),
      mine: z.boolean().optional(),
    })
    .nullable(),
})
export type InboxRow = z.infer<typeof inboxRowSchema>

export const inboxSchema = z.object({
  rows: z.array(inboxRowSchema),
  counts: z.object({ TO_DO: z.number(), UNREAD: z.number(), MINE: z.number(), ALL: z.number() }),
})
export type Inbox = z.infer<typeof inboxSchema>

const itemSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('INBOUND'),
    id: z.string(),
    at: z.string(),
    body: z.string(),
    automatic: z.boolean(),
  }),
  z.object({
    type: z.literal('OUTBOUND'),
    id: z.string(),
    at: z.string(),
    body: z.string(),
    state: z.enum(journalStates),
    reason: z.string().nullable(),
    errorCode: z.number().int().nullable(),
    providerStatus: z.string().nullable(),
    sourceModule: z.string(),
    scope: z.string(),
    templateName: z.string().nullable(),
    authorName: z.string().nullable(),
  }),
  z.object({
    type: z.literal('NOTE'),
    id: z.string(),
    at: z.string(),
    body: z.string(),
    authorName: z.string().nullable(),
  }),
  z.object({
    type: z.literal('EVENT'),
    id: z.string(),
    at: z.string(),
    kind: z.string(),
    fromValue: z.string().nullable(),
    toValue: z.string().nullable(),
    actorName: z.string().nullable(),
  }),
])
export type ConversationItem = z.infer<typeof itemSchema>

export const conversationSchema = z.object({
  conversationId: z.string(),
  contactId: z.string(),
  phone: z.string(),
  status: z.enum(conversationStatuses),
  assignedUserId: z.string().nullable(),
  assignedToMe: z.boolean(),
  assignedUserName: z.string().nullable(),
  version: z.number().int(),
  lastReadAt: z.string().nullable(),
  identity: identitySchema,
  consents: z.object({
    snowAlerts: z.enum(consentStates).nullable(),
    administrative: z.enum(consentStates).nullable(),
    localBlockReason: z.string().nullable(),
    localBlockedAt: z.string().nullable(),
    providerOptedOutAt: z.string().nullable(),
  }),
  composer: z.object({
    canReply: z.boolean(),
    blockReason: z.string().nullable(),
    windowHours: z.number().int().nullable(),
    mode: z.string().nullable(),
  }),
  items: z.array(itemSchema),
})
export type Conversation = z.infer<typeof conversationSchema>

export const replyResultSchema = z.object({
  result: z.object({
    state: z.string(),
    reason: z.string().nullable().optional(),
    replayed: z.boolean().optional(),
  }),
  conversation: conversationSchema,
})
export type ReplyResult = z.infer<typeof replyResultSchema>

export const linkCandidatesSchema = z.array(
  z.object({ clientId: z.string(), name: z.string(), address: z.string().nullable() }),
)
export type LinkCandidate = z.infer<typeof linkCandidatesSchema>[number]

export interface ConversationCommand {
  conversationId: string
  expectedVersion: number
}

const statusDisplay: Record<ConversationStatus, { label: string; tone: StatusTone }> = {
  TO_DO: { label: 'À traiter', tone: 'warning' },
  IN_PROGRESS: { label: 'En cours', tone: 'info' },
  WAITING_CUSTOMER: { label: 'En attente du client', tone: 'pending' },
  RESOLVED: { label: 'Résolu', tone: 'neutral' },
}

export function conversationStatusLabel(status: ConversationStatus): string {
  return statusDisplay[status].label
}

export function conversationStatusTone(status: ConversationStatus): StatusTone {
  return statusDisplay[status].tone
}

export const conversationStatusOptions = conversationStatuses.map((status) => ({
  value: status,
  label: conversationStatusLabel(status),
}))

export const inboxViewLabels: Record<InboxView, string> = {
  TO_DO: 'À traiter',
  UNREAD: 'Non lus',
  MINE: 'Mes conversations',
  ALL: 'Toutes',
}

/** Nom au premier plan : le dossier confirmé, sinon l'état d'identification. Jamais un nom deviné. */
export function conversationTitle(identity: ContactIdentity): string {
  switch (identity.kind) {
    case 'KNOWN':
      return identity.clients[0]?.name ?? 'Client'
    case 'SHARED':
      return 'Numéro partagé'
    case 'TO_CONFIRM':
      return 'Association à confirmer'
    case 'UNKNOWN':
      return 'Contact non identifié'
  }
}

export function identityFlag(identity: ContactIdentity): { label: string; tone: StatusTone } | null {
  if (identity.kind === 'UNKNOWN') return { label: 'Contact inconnu', tone: 'neutral' }
  if (identity.kind === 'TO_CONFIRM') return { label: 'Association à confirmer', tone: 'warning' }
  if (identity.kind === 'SHARED') return { label: 'Numéro partagé', tone: 'warning' }
  return null
}

export function previewText(preview: InboxRow['preview']): string {
  if (!preview) return ''
  if (preview.type === 'NOTE') return `Note : ${preview.body}`
  if (preview.type === 'OUTBOUND') return `${preview.mine ? 'Vous' : 'RECA'} : ${preview.body}`
  return preview.body
}

/** Raison pour laquelle aucune réponse ne peut partir ; jamais de bouton « Forcer ». */
export function composerBlockMessage(reason: string, windowHours: number | null): string {
  switch (reason) {
    case 'PROVIDER_OPTED_OUT':
      return 'Envoi impossible : ce numéro s’est désinscrit (STOP). Seule une reprise par le client (START) peut lever le blocage chez Telnyx.'
    case 'LOCAL_BLOCK':
      return 'Envoi impossible : ce numéro est bloqué (STOP ou retrait). Voir l’onglet Consentements.'
    case 'SENDING_PAUSED':
      return 'Nouveaux envois suspendus par un administrateur. La réception, les STOP et les statuts continuent.'
    case 'NO_RECENT_INBOUND':
      return `Réponse possible seulement dans les ${String(windowHours ?? 24)} h qui suivent un message du client.`
    case 'NOT_IN_TEST_ALLOWLIST':
      return 'Mode pilote : les réponses ne partent que vers les numéros d’équipe autorisés.'
    case 'MODULE_DISABLED':
      return 'Envois désactivés : le module SMS n’est pas en mode pilote ou production.'
    case 'DAILY_CAP_REACHED':
      return 'Plafond quotidien d’envois atteint : réessayez demain.'
    default:
      return `Envoi impossible : ${reasonLabel(reason)}.`
  }
}

const eventStatusLabel = (value: string | null) =>
  value && (conversationStatuses as readonly string[]).includes(value)
    ? conversationStatusLabel(value as ConversationStatus)
    : (value ?? '')

/** Ligne système du fil (changement de statut, responsable, rattachement). */
export function conversationEventLabel(item: Extract<ConversationItem, { type: 'EVENT' }>): string {
  const by = item.actorName ? ` par ${item.actorName}` : ''
  switch (item.kind) {
    case 'STATUS_CHANGED':
      return `${eventStatusLabel(item.fromValue)} → ${eventStatusLabel(item.toValue)}${by}`
    case 'REOPENED_BY_MESSAGE':
      return 'Nouveau message du client : remise à traiter'
    case 'ASSIGNED':
      return `Prise en charge par ${item.toValue ?? 'un membre de l’équipe'}`
    case 'UNASSIGNED':
      return `Plus de responsable${by}`
    case 'CLIENT_LINKED':
      return `Numéro rattaché au dossier ${item.toValue ?? ''}${by}`
    default:
      return item.kind
  }
}

const dayFormat = new Intl.DateTimeFormat('fr-CA', { dateStyle: 'full', timeZone: 'America/Toronto' })
const timeFormat = new Intl.DateTimeFormat('fr-CA', { timeStyle: 'short', timeZone: 'America/Toronto' })
const shortFormat = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'short',
  timeZone: 'America/Toronto',
})
const dayKeyFormat = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' })

export function formatTime(value: string): string {
  return timeFormat.format(new Date(value))
}

/** Heure si aujourd'hui, sinon date courte. */
export function formatListTime(value: string, now: Date = new Date()): string {
  const date = new Date(value)
  return dayKeyFormat.format(date) === dayKeyFormat.format(now)
    ? timeFormat.format(date)
    : shortFormat.format(date)
}

/** Fil groupé par jour (Montréal), dans l'ordre chronologique. */
export function groupByDay(
  items: ConversationItem[],
): { day: string; label: string; items: ConversationItem[] }[] {
  const groups: { day: string; label: string; items: ConversationItem[] }[] = []
  for (const item of items) {
    const date = new Date(item.at)
    const day = dayKeyFormat.format(date)
    const last = groups.at(-1)
    if (last?.day === day) last.items.push(item)
    else groups.push({ day, label: dayFormat.format(date), items: [item] })
  }
  return groups
}

/** Dernier message entrant non lu par la session : la conversation doit être marquée lue. */
export function hasUnreadInbound(conversation: Conversation): boolean {
  const lastRead = conversation.lastReadAt ? new Date(conversation.lastReadAt).getTime() : -Infinity
  return conversation.items.some(
    (item) => item.type === 'INBOUND' && !item.automatic && new Date(item.at).getTime() > lastRead,
  )
}

const gsmBasic =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const gsmExtension = '^{}\\[~]|€\f'

/** Estimation des segments, même règle que `sms_segment_count_v1` (GSM-7 ou UCS-2). Telnyx reste l'autorité. */
export function estimateSegments(text: string): {
  segments: number
  encoding: 'GSM-7' | 'UCS-2'
  units: number
} {
  let units = 0
  let gsm = true
  for (const char of text) {
    if (gsmBasic.includes(char)) units += 1
    else if (gsmExtension.includes(char)) units += 2
    else {
      gsm = false
      break
    }
  }
  if (gsm) return { segments: units <= 160 ? 1 : Math.ceil(units / 153), encoding: 'GSM-7', units }
  const utf16 = text.length
  return { segments: utf16 <= 70 ? 1 : Math.ceil(utf16 / 67), encoding: 'UCS-2', units: utf16 }
}

export function segmentsLabel(text: string): string {
  const estimate = estimateSegments(text)
  const plural = estimate.segments > 1 ? 's' : ''
  return `${String(text.length)} caractères · ${String(estimate.segments)} segment${plural} estimé${plural} (${estimate.encoding})`
}

/** Détail d'un envoi qui n'est pas simplement parti ou livré, sous la bulle. */
export function outboundDetail(item: Extract<ConversationItem, { type: 'OUTBOUND' }>): string | null {
  switch (item.state) {
    case 'BLOCKED':
    case 'CANCELLED':
      return `Non envoyé : ${reasonLabel(item.reason)}.`
    case 'EXPIRED':
      return 'Non envoyé : délai de validité dépassé. Le message ne sera pas rejoué.'
    case 'SEND_FAILED':
    case 'DELIVERY_FAILED':
      return item.errorCode
        ? `Refus de l’opérateur ou de Telnyx (code ${String(item.errorCode)}).`
        : 'Refus de l’opérateur ou de Telnyx.'
    case 'UNCONFIRMED':
      return 'L’opérateur n’a transmis aucun résultat final : ni succès ni échec confirmé.'
    case 'UNCERTAIN':
      return 'Soumission sans réponse de Telnyx : à vérifier, le message n’est pas renvoyé à l’aveugle.'
    case 'SCHEDULED':
    case 'PREPARED':
    case 'SUBMITTED':
    case 'DELIVERED':
      return null
  }
}
